<script lang="ts">
  import PageMeta from '$lib/components/PageMeta.svelte';
  import GridLines from '$lib/components/GridLines.svelte';
  import GridContent from '$lib/components/GridContent.svelte';
  import GridInner from '$lib/components/GridInner.svelte';
  import CornerFrame from '$lib/components/CornerFrame.svelte';
  import Star from '$lib/components/Star.svelte';
  import ContactCtaSection from '$lib/sections/ContactCtaSection.svelte';
  import { hatchStyle45 } from '$lib/styles/hatch';
  import { Cloud, Shield, Eye, Lock, ShieldCheck, Network } from '@lucide/svelte';

  // CRF walkthrough steps
  const crfSteps = [
    {
      step: '01',
      label: 'Exposure',
      description: 'An agent reads content from an untrusted source — an email, a webpage, a user-uploaded file.'
    },
    {
      step: '02',
      label: 'Detection',
      description: 'Hidden injections are surfaced as visible text requiring human review. The agent\'s reasoning is flagged as potentially tainted.'
    },
    {
      step: '03',
      label: 'Constraint',
      description: 'Subsequent actions are constrained: elevated approval requirements, restricted tool access, human-in-the-loop checkpoints.'
    },
    {
      step: '04',
      label: 'Audit',
      description: 'Every action, every decision, every data access — logged with full context. Immutable. Queryable.'
    }
  ];

  // Consolidated pillar cards
  const pillars = [
    {
      title: 'On-Tenant Deployment',
      subtitle: 'Your cloud. Your boundary.',
      description: 'Corral deploys directly into your Azure tenant. Your data never leaves your boundary — not by policy, by architecture. We can\'t access it.',
      icon: Cloud,
      details: ['Zero data egress', 'Inherit Azure certifications', 'Private endpoints & VNet integration']
    },
    {
      title: 'Cumulative Restrictions Framework',
      subtitle: 'Control what AI can do.',
      description: 'Governs agent actions based on what they\'ve been exposed to. Tool boundaries, action conditions, approval flows — applied automatically, every time.',
      icon: Shield,
      details: ['Per-agent permission scoping', 'Human-in-the-loop for high-risk actions', 'Content filtering & PII detection']
    },
    {
      title: 'Governance & Observability',
      subtitle: 'Full visibility. Total accountability.',
      description: 'Every conversation, every tool call, every decision — logged with full context. Compliance teams can answer any question. Security teams can investigate any incident.',
      icon: Eye,
      details: ['Immutable audit trail', 'Policy enforcement (input & output)', 'Real-time monitoring & alerting']
    }
  ];

  // Real-world examples from CPF page
  const examples = [
    { scenario: 'HR Agent', config: "Can read policies, can't access compensation data. Can create tickets, can't modify employee records." },
    { scenario: 'Support Agent', config: 'Can read customer history, can issue refunds under $100. Larger refunds require approval.' },
    { scenario: 'Executive Assistant', config: 'Can schedule meetings, send emails as draft. Calendar invites on behalf of exec need confirmation.' }
  ];
</script>

<PageMeta
  title="Security & Governance — Control what AI does. Not just what it sees."
  description="Full-stack security that governs agent behavior across every layer. The Cumulative Restrictions Framework constrains what AI can do after reading untrusted content."
/>

<!-- Top stars -->
<div class="relative">
  <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute left-[calc(9.03%-9px)] -top-px" />
  <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute right-[calc(9.03%-9px)] -top-px" />
</div>

<!-- Hero -->
<section class="bg-[#F9F9F4] py-[80px] lg:py-[120px] relative" style={hatchStyle45}>
  <GridLines variant="light" />
  <GridContent>
    <GridInner>
      <div class="max-w-[700px]">
        <h1 class="font-sans text-[48px] lg:text-[64px] tracking-[-0.04em] leading-[1.1] text-[#020202] mb-6">
          Control what AI does. Not just what it sees.
        </h1>
        <p class="font-mono font-light text-[16px] text-[#5C5855] leading-relaxed">
          Most AI security focuses on what the model can access. That's necessary. It's not sufficient. AI agents act — and governing agent behavior requires control across the entire stack.
        </p>
      </div>
    </GridInner>
  </GridContent>
</section>

<!-- Divider -->
<div class="h-[48px] bg-[#f9f9f4] relative" style={hatchStyle45}>
  <GridLines variant="light" />
  <div class="hidden lg:block absolute bottom-0 left-0 right-0 h-px bg-[#D3D3D3]" aria-hidden="true"></div>
  <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute left-[calc(9.03%-9px)] bottom-[-9px]" />
  <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute right-[calc(9.03%-9px)] bottom-[-9px]" />
</div>

<!-- §1: The Problem — Narrative prose, no cards -->
<section class="bg-[#0d0d0d] py-[80px] relative">
  <GridLines variant="dark" />
  <GridContent>
    <GridInner>
      <div class="max-w-[700px] mx-auto">
        <h2 class="font-sans text-[36px] lg:text-[48px] text-white tracking-[-0.04em] leading-[1.1] mb-8">
          Agents are a new type of user. And the hardest security problem has always been user behavior.
        </h2>
        <div class="flex flex-col gap-6">
          <p class="font-body font-light text-[16px] text-[#878787] leading-relaxed">
            Agents can be manipulated through their inputs: prompt injections from emails, tainted reasoning from web content, chained tool calls that propagate untrusted data into trusted systems.
          </p>
          <p class="font-body font-light text-[16px] text-[#878787] leading-relaxed">
            The good news: agents work in spaces we can control far more granularly than human users. The bad news: that control requires the full stack. And you can't safely customize AI behavior without controlling every layer.
          </p>
          <div class="my-4 border-l-2 border-white/20 pl-6 py-1">
            <p class="font-body text-[18px] text-white leading-relaxed">
              An agent reads a compromised email, acts on tainted reasoning, and propagates untrusted input into a trusted system. The attack surface isn't the model — it's the chain of actions afterward.
            </p>
          </div>
        </div>
      </div>
    </GridInner>
  </GridContent>
</section>

<!-- Divider -->
<div class="h-[48px] bg-[#0d0d0d] relative">
  <GridLines variant="dark" />
  <div class="hidden lg:block absolute bottom-0 left-0 right-0 h-px bg-[#333335]" aria-hidden="true"></div>
  <Star variant="full" class="hidden lg:block absolute left-[calc(9.03%-9px)] bottom-[-9px]" />
  <Star variant="full" class="hidden lg:block absolute right-[calc(9.03%-9px)] bottom-[-9px]" />
</div>

<!-- §2: CRF Walkthrough — 4-step visual -->
<section class="bg-[#0d0d0d] py-[80px] relative">
  <GridLines variant="dark" />
  <GridContent>
    <GridInner>
      <div class="max-w-[700px] mx-auto mb-12">
        <h2 class="font-sans text-[36px] lg:text-[48px] text-white tracking-[-0.04em] leading-[1.1] mb-6">
          Secure by architecture. Not by policy.
        </h2>
        <p class="font-body font-light text-[16px] text-[#878787] leading-relaxed">
          Corral's Cumulative Restrictions Framework tracks what an agent has been exposed to and constrains what it can do next. This isn't a filter you bolt on. It's the architecture — and it's what makes safe extensibility possible.
        </p>
      </div>

      <!-- Steps -->
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-0">
        {#each crfSteps as step, i}
          <div class="bg-[#171719] border border-[#333335] p-6 {i > 0 ? 'lg:-ml-px' : ''} {i >= 2 ? '-mt-px lg:mt-0' : ''}">
            <span class="font-mono text-[12px] text-[#ee6018] mb-3 block">{step.step}</span>
            <h3 class="font-sans text-[18px] text-white mb-3">{step.label}</h3>
            <p class="font-mono font-light text-[13px] text-[#878787] leading-relaxed">{step.description}</p>
          </div>
        {/each}
      </div>
    </GridInner>
  </GridContent>
</section>

<!-- Divider -->
<div class="h-[48px] bg-[#f9f9f4] relative" style={hatchStyle45}>
  <GridLines variant="light" />
  <div class="hidden lg:block absolute top-0 left-0 right-0 h-px bg-[#D3D3D3]" aria-hidden="true"></div>
  <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute left-[calc(9.03%-9px)] top-0" />
  <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute right-[calc(9.03%-9px)] top-0" />
</div>

<!-- §3: Three Pillars -->
<section class="bg-[#f9f9f4] py-[80px] relative" style={hatchStyle45}>
  <GridLines variant="light" />
  <GridContent>
    <GridInner>
      <div class="relative">
        <!-- Horizontal lines -->
        <div class="hidden lg:block absolute top-0 -left-[calc(54px+9.03vw)] -right-[calc(54px+9.03vw)] h-px bg-[#D3D3D3] z-[5]" aria-hidden="true"></div>
        <Star variant="full" color="#D3D3D3" class="hidden lg:block absolute -left-[63px] top-[-9px]" />
        <Star variant="full" color="#D3D3D3" class="hidden lg:block absolute -right-[63px] top-[-9px]" />

        <div class="hidden lg:block absolute bottom-0 -left-[calc(54px+9.03vw)] -right-[calc(54px+9.03vw)] h-px bg-[#D3D3D3] z-[5]" aria-hidden="true"></div>
        <Star variant="full" color="#D3D3D3" class="hidden lg:block absolute -left-[63px] bottom-[-9px]" />
        <Star variant="full" color="#D3D3D3" class="hidden lg:block absolute -right-[63px] bottom-[-9px]" />

        <div class="grid grid-cols-1 lg:grid-cols-3 gap-0">
          {#each pillars as pillar, i}
            <div class="bg-white border border-[#D3D3D3] p-8 {i > 0 ? 'lg:-ml-px' : ''} {i >= 1 ? '-mt-px lg:mt-0' : ''}">
              <div class="flex items-center gap-3 mb-4">
                <CornerFrame
                  class="w-[43px] h-[44px] flex items-center justify-center"
                  bg="bg-transparent"
                  borderColor="rgba(0, 0, 0, 0.15)"
                  cornerColor="#020202"
                  cornerSize={7}
                >
                  <svelte:component this={pillar.icon} class="w-4 h-4 text-[#020202]" />
                </CornerFrame>
                <div>
                  <h3 class="font-sans text-[16px] text-[#020202]">{pillar.title}</h3>
                  <p class="font-mono text-[12px] text-[#ee6018]">{pillar.subtitle}</p>
                </div>
              </div>
              <p class="font-mono font-light text-[14px] text-[#5C5855] leading-relaxed mb-5">
                {pillar.description}
              </p>
              <ul class="flex flex-col gap-2">
                {#each pillar.details as detail}
                  <li class="font-mono font-light text-[13px] text-[#020202] flex items-center gap-2">
                    <span class="w-1 h-1 bg-[#ee6018] rounded-full shrink-0"></span>
                    {detail}
                  </li>
                {/each}
              </ul>
            </div>
          {/each}
        </div>
      </div>
    </GridInner>
  </GridContent>
</section>

<!-- Divider -->
<div class="h-[48px] bg-[#0d0d0d] relative">
  <GridLines variant="dark" />
  <div class="hidden lg:block absolute top-0 left-0 right-0 h-px bg-[#D3D3D3]" aria-hidden="true"></div>
  <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute left-[calc(9.03%-9px)] top-0" />
  <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute right-[calc(9.03%-9px)] top-0" />
</div>

<!-- §4: Real-world examples -->
<section class="bg-[#0d0d0d] py-[80px] relative">
  <GridLines variant="dark" />
  <GridContent>
    <GridInner>
      <div class="max-w-[700px] mx-auto mb-12">
        <h2 class="font-sans text-[36px] lg:text-[48px] text-white tracking-[-0.04em] leading-[1.1] mb-6">
          Guardrails that make sense.
        </h2>
        <p class="font-body font-light text-[16px] text-[#878787] leading-relaxed">
          Every agent gets scoped permissions that match its role. Not blanket restrictions — precise, contextual boundaries.
        </p>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
        {#each examples as example}
          <div class="bg-[#171719] border border-[#333335] p-6">
            <h3 class="font-sans text-[16px] text-white mb-3">{example.scenario}</h3>
            <p class="font-mono font-light text-[13px] text-[#878787] leading-relaxed">
              {example.config}
            </p>
          </div>
        {/each}
      </div>
    </GridInner>
  </GridContent>
</section>

<!-- Divider -->
<div class="h-[48px] bg-[#0d0d0d] relative">
  <GridLines variant="dark" />
  <div class="hidden lg:block absolute bottom-0 left-0 right-0 h-px bg-[#333335]" aria-hidden="true"></div>
  <Star variant="full" class="hidden lg:block absolute left-[calc(9.03%-9px)] bottom-[-9px]" />
  <Star variant="full" class="hidden lg:block absolute right-[calc(9.03%-9px)] bottom-[-9px]" />
</div>

<!-- §5: Zero egress callout -->
<section class="bg-[#0d0d0d] py-[80px] relative">
  <GridLines variant="dark" />
  <GridContent>
    <GridInner>
      <div class="max-w-[700px] mx-auto text-center">
        <div class="mb-10 border-l-2 border-[#ee6018] pl-6 py-2 text-left">
          <p class="font-mono font-light text-[18px] text-white leading-relaxed">
            Your data never leaves your tenant. Not by policy — by architecture. We can't access it. Your AI runs on your infrastructure, behind your network, governed by your identity provider. Zero data egress, full audit trail, tenant isolation by default.
          </p>
        </div>
        <p class="font-mono font-light text-[14px] text-[#878787] leading-relaxed">
          You can customize AI behavior without compromising the security model underneath. That's what safe extensibility means — and it's only possible when security is the architecture, not a bolt-on.
        </p>
      </div>
    </GridInner>
  </GridContent>
</section>

<!-- Divider: → CTA -->
<div class="h-[48px] bg-[#0d0d0d] relative">
  <GridLines variant="dark" />
  <div class="hidden lg:block absolute bottom-0 left-0 right-0 h-px bg-[#D3D3D3]" aria-hidden="true"></div>
  <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute left-[calc(9.03%-9px)] bottom-[-9px]" />
  <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute right-[calc(9.03%-9px)] bottom-[-9px]" />
</div>

<!-- CTA -->
<ContactCtaSection />
